import type { CSSProperties } from 'react';
import type { TargetAndTransition, Transition } from 'framer-motion';
import { RadiusSize, getRadiusCssValue, getRadiusValue } from '@/theme/radius';
import { pxToRem } from '@/theme/spacing';
import { ENTRANCE_ANIMATIONS } from '@/utils/animation';
import {
  CIMAGE_ANIMATION,
  CIMAGE_BORDER_RADIUS_INHERIT,
  CIMAGE_OBJECT_FIT,
  CIMAGE_OVERFLOW_HIDDEN,
  CIMAGE_SIZE_AUTO,
  CIMAGE_SIZE_FULL,
  SVG_EXTENSION,
  type CimageAnimation,
  type CimageObjectFit,
} from './cimage-constants';

const CIMAGE_OBJECT_FIT_VALUES = new Set<string>(
  Object.values(CIMAGE_OBJECT_FIT),
);
const EMPTY_STYLE: CSSProperties = {};

interface ImageDimensionModelInput {
  aspectRatio?: number;
  height?: number | string;
  width?: number | string;
}

export interface ImageDimensionModel {
  derivedHeight?: number;
  derivedWidth?: number;
  hasNumericDims: boolean;
  heightValue?: string;
  numericHeight?: number;
  numericWidth?: number;
  widthValue?: string;
}

interface RootStyleInput extends ImageDimensionModelInput {
  radiusValue?: number;
  showSkeleton: boolean;
  style?: CSSProperties;
}

interface ImageStyleInput extends ImageDimensionModelInput {
  objectFit: CimageObjectFit;
  style?: CSSProperties;
}

interface ImageMotionStateInput {
  animation: CimageAnimation;
  isLoaded: boolean;
}

interface ImageAxisSizeInput {
  rootHasSize: boolean;
  styleSize?: CSSProperties['width'];
}

interface StyleLayoutValues {
  aspectRatio?: CSSProperties['aspectRatio'];
  borderRadius?: CSSProperties['borderRadius'];
  height?: CSSProperties['height'];
  maxHeight?: CSSProperties['maxHeight'];
  maxWidth?: CSSProperties['maxWidth'];
  minHeight?: CSSProperties['minHeight'];
  minWidth?: CSSProperties['minWidth'];
  width?: CSSProperties['width'];
}

export interface ImageMotionState {
  imageAnimate: false | TargetAndTransition;
  imageExit: TargetAndTransition;
  imageInitial: false | TargetAndTransition;
}

export function toCssSize(value: number | string | undefined): string | undefined {
  if (value == null) {
    return undefined;
  }

  return typeof value === 'number' ? pxToRem(value) : value;
}

export function toImageDimension(
  value: number | string | undefined,
): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

export function resolveRadiusValue(
  radius: number | RadiusSize | undefined,
): number | undefined {
  if (radius == null) {
    return undefined;
  }

  return typeof radius === 'number' ? radius : getRadiusValue(radius);
}

export function objectFitFromStyle(
  style: CSSProperties | undefined,
): CimageObjectFit | undefined {
  const { objectFit } = style ?? EMPTY_STYLE;
  return typeof objectFit === 'string' && CIMAGE_OBJECT_FIT_VALUES.has(objectFit)
    ? objectFit as CimageObjectFit
    : undefined;
}

export function isSvgSource(src: string): boolean {
  return src.toLowerCase().endsWith(SVG_EXTENSION);
}

export function getImageDimensionModel({
  aspectRatio,
  height,
  width,
}: ImageDimensionModelInput): ImageDimensionModel {
  const widthValue = toCssSize(width);
  const heightValue = toCssSize(height);
  const numericWidth = toImageDimension(width);
  const numericHeight = toImageDimension(height);
  const derivedWidth = getDerivedWidth({
    aspectRatio,
    numericHeight,
    numericWidth,
  });
  const derivedHeight = getDerivedHeight({
    aspectRatio,
    numericHeight,
    numericWidth,
  });

  return {
    derivedHeight,
    derivedWidth,
    hasNumericDims: derivedWidth != null && derivedHeight != null,
    heightValue,
    numericHeight,
    numericWidth,
    widthValue,
  };
}

function getDerivedWidth(input: {
  aspectRatio?: number;
  numericHeight?: number;
  numericWidth?: number;
}): number | undefined {
  if (input.numericWidth != null) {
    return input.numericWidth;
  }

  return input.numericHeight && input.aspectRatio
    ? Math.round(input.numericHeight * input.aspectRatio)
    : undefined;
}

function getDerivedHeight(input: {
  aspectRatio?: number;
  numericHeight?: number;
  numericWidth?: number;
}): number | undefined {
  if (input.numericHeight != null) {
    return input.numericHeight;
  }

  return input.numericWidth && input.aspectRatio
    ? Math.round(input.numericWidth / input.aspectRatio)
    : undefined;
}

function getRootOverflow(input: {
  radiusValue?: number;
  showSkeleton: boolean;
}): CSSProperties['overflow'] {
  return input.radiusValue != null || input.showSkeleton
    ? CIMAGE_OVERFLOW_HIDDEN
    : undefined;
}

function getRootBorderRadius(input: {
  radiusValue?: number;
  styleValues: StyleLayoutValues;
}): CSSProperties['borderRadius'] {
  return input.radiusValue != null
    ? getRadiusCssValue(input.radiusValue)
    : input.styleValues.borderRadius;
}

function getStyleLayoutValues(style: CSSProperties | undefined): StyleLayoutValues {
  const {
    aspectRatio,
    borderRadius,
    height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    width,
  } = style ?? EMPTY_STYLE;

  return {
    aspectRatio,
    borderRadius,
    height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    width,
  };
}

export function buildRootStyle({
  aspectRatio,
  heightValue,
  radiusValue,
  showSkeleton,
  style,
  widthValue,
}: RootStyleInput & Pick<ImageDimensionModel, 'heightValue' | 'widthValue'>): CSSProperties {
  const styleValues = getStyleLayoutValues(style);

  return {
    position: 'relative',
    display: 'inline-block',
    lineHeight: 0,
    overflow: getRootOverflow({ radiusValue, showSkeleton }),
    ...style,
    width: styleValues.width ?? widthValue,
    height: styleValues.height ?? heightValue,
    aspectRatio: styleValues.aspectRatio ?? aspectRatio,
    borderRadius: getRootBorderRadius({ radiusValue, styleValues }),
  };
}

function hasRootAxisSize(input: {
  propValue?: number | string;
  styleSize?: CSSProperties['width'];
  aspectRatio?: number;
}): boolean {
  return input.propValue != null ||
    input.styleSize != null ||
    input.aspectRatio != null;
}

function getImageAxisSize(input: ImageAxisSizeInput): CSSProperties['width'] {
  if (input.styleSize === CIMAGE_SIZE_AUTO) {
    return CIMAGE_SIZE_AUTO;
  }

  return input.rootHasSize ? CIMAGE_SIZE_FULL : input.styleSize;
}

export function buildImageStyle({
  aspectRatio,
  height,
  objectFit,
  style,
  width,
}: ImageStyleInput): CSSProperties {
  const styleValues = getStyleLayoutValues(style);
  const hasRootWidth = hasRootAxisSize({
    propValue: width,
    styleSize: styleValues.width,
    aspectRatio,
  });
  const hasRootHeight = hasRootAxisSize({
    propValue: height,
    styleSize: styleValues.height,
    aspectRatio,
  });

  return {
    display: 'block',
    width: getImageAxisSize({
      rootHasSize: hasRootWidth,
      styleSize: styleValues.width,
    }),
    height: getImageAxisSize({
      rootHasSize: hasRootHeight,
      styleSize: styleValues.height,
    }),
    maxWidth: styleValues.maxWidth,
    maxHeight: styleValues.maxHeight,
    minWidth: styleValues.minWidth,
    minHeight: styleValues.minHeight,
    objectFit,
    borderRadius: CIMAGE_BORDER_RADIUS_INHERIT,
  };
}

function getFadeBlurMotionState(input: {
  isLoaded: boolean;
}): ImageMotionState {
  return {
    imageInitial: ENTRANCE_ANIMATIONS.fadeBlur.initial,
    imageAnimate: input.isLoaded
      ? ENTRANCE_ANIMATIONS.fadeBlur.animate
      : ENTRANCE_ANIMATIONS.fadeBlur.initial,
    imageExit: ENTRANCE_ANIMATIONS.fadeBlur.exit,
  };
}

function getStillMotionState(): ImageMotionState {
  return {
    imageInitial: false,
    imageAnimate: { opacity: 1, filter: 'blur(0px)' },
    imageExit: { opacity: 0 },
  };
}

export function getImageMotionState({
  animation,
  isLoaded,
}: ImageMotionStateInput): ImageMotionState {
  return animation === CIMAGE_ANIMATION.FadeBlur
    ? getFadeBlurMotionState({ isLoaded })
    : getStillMotionState();
}

export function buildStaticMotionStyle(input: {
  hasNumericDims: boolean;
}): CSSProperties {
  return {
    display: 'block',
    position: input.hasNumericDims ? 'relative' : 'absolute',
    inset: input.hasNumericDims ? undefined : 0,
    width: CIMAGE_SIZE_FULL,
    height: CIMAGE_SIZE_FULL,
  };
}

export type CimageTransition = Transition;
